import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_TONES = {
  cohort_application: '#C8A84B',
  chapter_lead: '#9B6F2A',
  mentor_booking: '#3A4A5C',
  forum_payment: '#0D1B2A',
  investor_signup: '#7A6940',
  community_request: '#1A2F4A',
  webinar_registration: '#5C6E80',
};

const STATUS_TONES = {
  new: '#C8A84B',
  contacted: '#3A4A5C',
  qualified: '#0D1B2A',
  converted: '#2D8657',
  dropped: '#7A8190',
};

export default function AdminLeadsPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('admin_api_key') || '');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState({ lead_type: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    document.title = 'Admin · Lead Pipeline · SKI AXIS';
    if (adminKey) tryAuth(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryAuth = async (key) => {
    setErr(''); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/stats`, { params: { admin_key: key } });
      setStats(data);
      setAuthed(true);
      localStorage.setItem('admin_api_key', key);
      await loadLeads(key, {});
    } catch (e) {
      setErr(e?.response?.status === 403 ? 'Invalid admin key.' : 'Could not load.');
      setAuthed(false);
    } finally { setLoading(false); }
  };

  const loadLeads = async (key, f) => {
    setLoading(true);
    try {
      const params = { admin_key: key, limit: 500, ...Object.fromEntries(Object.entries(f).filter(([_, v]) => v)) };
      const { data } = await axios.get(`${API}/admin/leads`, { params });
      setLeads(data.leads || []);
    } catch (e) { setErr('Failed to load leads.'); }
    finally { setLoading(false); }
  };

  const onFilter = (k, v) => {
    const next = { ...filter, [k]: v };
    setFilter(next);
    loadLeads(adminKey, next);
  };

  const updateStatus = async (lead_id, newStatus) => {
    try {
      await axios.post(`${API}/admin/leads/${lead_id}/status`, null, { params: { admin_key: adminKey, status: newStatus } });
      await loadLeads(adminKey, filter);
      const { data } = await axios.get(`${API}/admin/leads/stats`, { params: { admin_key: adminKey } });
      setStats(data);
    } catch (e) { alert('Status update failed.'); }
  };

  const sortedTypes = useMemo(() => {
    if (!stats?.by_type) return [];
    return Object.entries(stats.by_type).map(([key, t]) => ({ key, ...t })).sort((a, b) => b.total - a.total);
  }, [stats]);

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#FAF7F0', padding: '60px 20px', fontFamily: 'Georgia, serif', color: '#0D1B2A' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', background: '#FFFFFF', padding: '44px 40px', border: '1px solid rgba(200,168,75,.35)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.32em', color: '#C8A84B', textTransform: 'uppercase', marginBottom: 14 }}>SKI AXIS · Admin Console</div>
          <h1 style={{ fontFamily: '"Cormorant Garamond",serif', fontSize: 36, fontWeight: 700, lineHeight: 1.1, margin: '0 0 18px', letterSpacing: '-.012em' }}>Lead Pipeline</h1>
          <p style={{ fontSize: 14, color: '#3A4A5C', lineHeight: 1.6, margin: '0 0 22px' }}>Single source of truth for every lead — Cohort applications, Chapter Leads, Mentor bookings, Forum payments, Investor signups.</p>
          <input type="password" placeholder="Admin API key" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && tryAuth(adminKey)} data-testid="admin-key-input" style={{ width: '100%', padding: '12px 14px', fontSize: 14, fontFamily: 'Georgia,serif', background: '#FAF7F0', border: '1px solid rgba(13,27,42,.18)', boxSizing: 'border-box', borderRadius: 0, marginBottom: 14 }} />
          {err && <div style={{ color: '#D63031', fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <button onClick={() => tryAuth(adminKey)} disabled={loading || !adminKey} data-testid="admin-key-submit" style={{ background: '#0D1B2A', color: '#C8A84B', padding: '12px 22px', fontSize: 11, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', border: '1px solid #C8A84B', cursor: 'pointer', fontFamily: 'Georgia,serif' }}>{loading ? 'Checking…' : 'Sign In →'}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FAF7F0', color: '#0D1B2A', fontFamily: 'Georgia, serif' }}>
      <div style={{ maxWidth: 1380, margin: '0 auto', padding: '24px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <Link to="/" style={{ fontFamily: '"Cormorant Garamond",serif', fontSize: 22, fontWeight: 700, color: '#0D1B2A', textDecoration: 'none' }}><strong>SKI</strong> <em style={{ color: '#C8A84B', fontStyle: 'italic' }}>AXIS</em> · Admin</Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase' }}>
          <Link to="/admin/webinars" style={{ color: '#3A4A5C', textDecoration: 'none', padding: '6px 12px' }}>Webinars</Link>
          <span style={{ color: '#C8A84B', padding: '6px 12px', borderBottom: '2px solid #C8A84B' }}>Leads</span>
          <button onClick={() => tryAuth(adminKey)} style={{ background: 'transparent', border: '1px solid rgba(200,168,75,.4)', padding: '8px 14px', cursor: 'pointer', fontFamily: 'Georgia,serif', fontWeight: 700, fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase' }}>↻ Refresh</button>
          <button onClick={() => { localStorage.removeItem('admin_api_key'); setAuthed(false); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3A4A5C', fontFamily: 'Georgia,serif', fontWeight: 700, fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase' }}>Sign Out</button>
        </div>
      </div>

      <section style={{ maxWidth: 1380, margin: '0 auto', padding: '24px 40px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.32em', color: '#C8A84B', textTransform: 'uppercase', marginBottom: 12 }}>Live Lead Pipeline · Auto-Follow-Up Active</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond",serif', fontSize: 48, fontWeight: 700, lineHeight: 1.06, margin: '0 0 8px', letterSpacing: '-.018em' }}>{stats?.total_leads ?? 0} <em style={{ color: '#C8A84B', fontStyle: 'italic' }}>active leads</em></h1>
        <p style={{ fontSize: 14, color: '#3A4A5C', margin: '0 0 32px' }}>Across {sortedTypes.length} channel{sortedTypes.length === 1 ? '' : 's'}. Auto-follow-ups fire at T+24h and T+72h until you mark a lead converted or dropped.</p>

        {/* TYPE CARDS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
          {sortedTypes.length === 0 && (
            <div style={{ padding: '40px 32px', background: '#FFFFFF', border: '1px solid rgba(200,168,75,.3)', textAlign: 'center', color: '#7A8190', fontStyle: 'italic', fontSize: 14, gridColumn: '1 / -1' }}>No leads yet. The first one will surface here in real time.</div>
          )}
          {sortedTypes.map((t) => (
            <div key={t.key} onClick={() => onFilter('lead_type', t.key)} data-testid={`leadtype-${t.key}`} style={{ padding: '20px 22px', background: filter.lead_type === t.key ? '#0D1B2A' : '#FFFFFF', color: filter.lead_type === t.key ? '#FAF7F0' : '#0D1B2A', border: '1px solid rgba(200,168,75,.4)', cursor: 'pointer', transition: 'all .25s ease' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.2em', color: filter.lead_type === t.key ? '#C8A84B' : TYPE_TONES[t.key] || '#0D1B2A', textTransform: 'uppercase', marginBottom: 8 }}>{t.icon} {t.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ fontFamily: '"Cormorant Garamond",serif', fontSize: 36, fontWeight: 700, lineHeight: 1, color: '#C8A84B', letterSpacing: '-.018em' }}>{t.total}</span>
                <span style={{ fontSize: 11, letterSpacing: '.1em', color: filter.lead_type === t.key ? 'rgba(250,247,240,.6)' : '#7A8190', textTransform: 'uppercase' }}>leads</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {Object.entries(t.by_status || {}).map(([s, c]) => (
                  <span key={s} style={{ fontSize: 10, padding: '2px 6px', background: filter.lead_type === t.key ? 'rgba(200,168,75,.15)' : '#FAF7F0', color: STATUS_TONES[s] || '#0D1B2A', border: `1px solid ${STATUS_TONES[s] || '#0D1B2A'}`, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>{s} · {c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,.3)', padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.24em', color: '#C8A84B', textTransform: 'uppercase' }}>Filter</div>
          <select value={filter.lead_type} onChange={(e) => onFilter('lead_type', e.target.value)} data-testid="filter-type" style={{ padding: '8px 12px', fontSize: 13, fontFamily: 'Georgia,serif', background: '#FAF7F0', border: '1px solid rgba(13,27,42,.15)', borderRadius: 0 }}>
            <option value="">All channels</option>
            {sortedTypes.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
          </select>
          <select value={filter.status} onChange={(e) => onFilter('status', e.target.value)} data-testid="filter-status" style={{ padding: '8px 12px', fontSize: 13, fontFamily: 'Georgia,serif', background: '#FAF7F0', border: '1px solid rgba(13,27,42,.15)', borderRadius: 0 }}>
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="converted">Converted (stops follow-up)</option>
            <option value="dropped">Dropped (stops follow-up)</option>
          </select>
          {(filter.lead_type || filter.status) && (
            <button onClick={() => { setFilter({ lead_type: '', status: '' }); loadLeads(adminKey, {}); }} style={{ background: 'transparent', border: 'none', color: '#D63031', fontSize: 12, cursor: 'pointer', fontFamily: 'Georgia,serif', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase' }}>× Clear</button>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#7A8190', fontStyle: 'italic' }}>{leads.length} lead{leads.length === 1 ? '' : 's'}</div>
        </div>

        {/* TABLE */}
        <div style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,.3)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0D1B2A', color: '#C8A84B' }}>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Type</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Name · Org</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Email</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Country</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Notes</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Follow-up</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Brief</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>Status</th>
                <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700 }}>When</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && !loading && (
                <tr><td colSpan={9} style={{ padding: '40px 20px', textAlign: 'center', color: '#7A8190', fontStyle: 'italic' }}>No leads match these filters.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} style={{ borderBottom: '1px solid #FAF0DC' }}>
                  <td style={{ padding: '10px 14px' }}><span style={{ fontSize: 10, padding: '2px 8px', color: TYPE_TONES[l.lead_type] || '#0D1B2A', border: `1px solid ${TYPE_TONES[l.lead_type] || '#0D1B2A'}`, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>{l.label}</span></td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{l.full_name}{l.organisation && l.organisation !== '—' ? <><br /><span style={{ fontSize: 11, color: '#7A8190', fontWeight: 400 }}>{l.organisation}</span></> : null}</td>
                  <td style={{ padding: '10px 14px' }}><a href={`mailto:${l.email}`} style={{ color: '#0D1B2A', borderBottom: '1px solid #C8A84B', textDecoration: 'none' }}>{l.email}</a></td>
                  <td style={{ padding: '10px 14px' }}>{l.country || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, maxWidth: 240, color: '#3A4A5C' }}>{l.notes ? (l.notes.length > 80 ? l.notes.slice(0, 80) + '…' : l.notes) : '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 11 }}>
                    <div>24h: {l.followup_24h_sent ? '✓' : '—'}</div>
                    <div>72h: {l.followup_72h_sent ? '✓' : '—'}</div>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 11 }} data-testid={`lead-brief-${l.id}`}>
                    {l.brief_sent ? (
                      <span
                        title={`${l.brief_filename || 'Brief'}${l.brief_sent_at ? ' · ' + new Date(l.brief_sent_at).toLocaleString() : ''}`}
                        style={{ display: 'inline-block', padding: '3px 9px', background: 'rgba(200,168,75,.18)', color: '#8A6A14', border: '1px solid #C8A84B', fontWeight: 700, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase' }}
                      >
                        ✓ Delivered
                      </span>
                    ) : (
                      <span style={{ color: '#A89F8E', fontSize: 11, fontStyle: 'italic' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} style={{ padding: '4px 8px', fontSize: 11.5, fontFamily: 'Georgia,serif', color: STATUS_TONES[l.status] || '#0D1B2A', background: '#FAF7F0', border: `1px solid ${STATUS_TONES[l.status] || '#0D1B2A'}`, borderRadius: 0, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="qualified">qualified</option>
                      <option value="converted">converted</option>
                      <option value="dropped">dropped</option>
                    </select>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 11.5, color: '#7A8190' }}>{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 28, padding: '18px 22px', background: 'rgba(200,168,75,.08)', border: '1px solid rgba(200,168,75,.3)', fontSize: 13, color: '#3A4A5C', lineHeight: 1.6 }}>
          <strong style={{ color: '#0D1B2A' }}>Auto-follow-up sequence active.</strong> Every lead receives a personal email at T+24h ("Following up on your application") and T+72h (final warm touch) unless you mark them <strong>converted</strong> or <strong>dropped</strong>. Both stop the sequence immediately.
        </div>
      </section>
    </div>
  );
}
